import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  useColorModeValue as mode,
  useDisclosure,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Link as ReactLink } from "react-router-dom";

const JobLogRow = ({ status, title, description, date, time, photos = [], index, header, log, onEdit, onDelete, jobNo, showJobNo = false, photoCount = 0 }) => {
  const [previewIndex, setPreviewIndex] = useState(-1);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const openPreview = (photoIndex) => {
    setPreviewIndex(photoIndex);
    onOpen();
  };

  const handleNextImage = () => {
    setPreviewIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  const handlePrevImage = () => {
    setPreviewIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleDelete = () => {
    if (!onDelete || !log) return;
    const confirmed = window.confirm("Are you sure you want to delete this job log entry?");
    if (confirmed) {
      onDelete(log);
    }
  };

  return (
    <>
      <Flex
        bg={index % 2 === 0 ? mode("white", "gray.800") : mode("gray.100", "gray.700")}
        align='center'
        justify='start'
        borderBottom='1px solid'
        borderColor={mode("gray.200", "gray.600")}
        alignItems='stretch'
      >
        {showJobNo && (
          <Box flex='1' borderRight='1px solid' borderColor={mode("gray.200", "gray.600")}>
            {header ? (
              <Text textAlign='left' fontWeight='bold' p={1}>
                Job No
              </Text>
            ) : (
              <ReactLink to={`/job/${jobNo}`}>
                <Text textAlign='left' fontWeight='semibold' p={1} color='blue.500' _hover={{ textDecoration: "underline" }} cursor='pointer'>
                  {jobNo}
                </Text>
              </ReactLink>
            )}
          </Box>
        )}
        <Box flex='2' borderRight='1px solid' borderColor={mode("gray.200", "gray.600")}>
          <Text textAlign='left' fontWeight={header ? "bold" : "normal"} p={1}>
            {date}
          </Text>
        </Box>
        <Box flex='1' borderRight='1px solid' borderColor={mode("gray.200", "gray.600")}>
          <Text textAlign='left' fontWeight={header ? "bold" : "normal"} p={1}>
            {time}
          </Text>
        </Box>
        <Box flex='1' borderRight='1px solid' borderColor={mode("gray.200", "gray.600")}>
          <Text textAlign='left' fontWeight={header ? "bold" : "normal"} p={1}>
            {status}
          </Text>
        </Box>
        <Box flex='3' borderRight='1px solid' borderColor={mode("gray.200", "gray.600")}>
          <Text flex='2' textAlign='left' fontWeight={header ? "bold" : "normal"} p={1}>
            {title}
          </Text>
        </Box>
        <Box flex='6' borderRight='1px solid' borderColor={mode("gray.200", "gray.600")}>
          <Text flex='4' textAlign='left' fontWeight={header ? "bold" : "normal"} p={1}>
            {description}
          </Text>
        </Box>
        <Box flex='2' borderRight='1px solid' borderColor={mode("gray.200", "gray.600")}>
          {header ? (
            <Text textAlign='left' fontWeight='bold' p={1}>
              Images
            </Text>
          ) : photos && photos.length > 0 ? (
            <Flex gap={2} flexWrap='wrap' p={1}>
              {photos.map((photo, photoIndex) => (
                <Image
                  key={`${photo.name || "photo"}-${photoIndex}`}
                  src={photo.data}
                  alt={photo.name || `Image ${photoIndex + 1}`}
                  boxSize='50px'
                  objectFit='cover'
                  borderRadius='md'
                  cursor='pointer'
                  _hover={{ opacity: 0.85 }}
                  onClick={() => openPreview(photoIndex)}
                />
              ))}
            </Flex>
          ) : photoCount > 0 && (!photos || photos.length === 0) ? (
            // Photos are loading - show spinner with count
            <HStack spacing={2} p={1}>
              <Spinner size='sm' color='blue.500' />
              <Text fontSize='sm' color='blue.600' fontWeight='medium'>
                Loading {photoCount} {photoCount === 1 ? "photo" : "photos"}...
              </Text>
            </HStack>
          ) : photoCount > 0 ? (
            <Badge colorScheme='blue' p={1}>
              📷 {photoCount}
            </Badge>
          ) : (
            <Text textAlign='left' p={1} color={mode("gray.500", "gray.300")}>
              None
            </Text>
          )}
        </Box>
        <Box flex='1' borderRight='1px solid' borderColor={mode("gray.200", "gray.600")}>
          {header ? (
            <Text textAlign='left' fontWeight='bold' p={1}>
              Actions
            </Text>
          ) : (
            <HStack spacing={2} p={1} justify='flex-start'>
              <IconButton
                aria-label='Edit log'
                icon={<EditIcon />}
                size='sm'
                colorScheme='blue'
                variant='ghost'
                onClick={() => onEdit && onEdit(log)}
              />
              <IconButton
                aria-label='Delete log'
                icon={<DeleteIcon />}
                size='sm'
                colorScheme='red'
                variant='ghost'
                onClick={handleDelete}
              />
            </HStack>
          )}
        </Box>
      </Flex>
      <Modal isOpen={isOpen} onClose={onClose} size='6xl' isCentered>
        <ModalOverlay bg='blackAlpha.800' />
        <ModalContent bg='transparent' boxShadow='none'>
          <ModalCloseButton color='white' size='lg' />
          <ModalBody p={0} display='flex' justifyContent='center' alignItems='center' minH='100vh' bg='black'>
            {previewIndex >= 0 && photos?.[previewIndex] && (
              <HStack spacing={4} w='100%' justify='center' align='center'>
                {photos.length > 1 && (
                  <IconButton
                    aria-label='Previous image'
                    icon={<ChevronLeftIcon w={10} h={10} />}
                    size='2xl'
                    isRound
                    bg='rgba(0, 0, 0, 0.8)'
                    color='white'
                    _hover={{ bg: "rgba(0, 0, 0, 0.95)" }}
                    onClick={handlePrevImage}
                  />
                )}
                <Image
                  src={photos[previewIndex].data}
                  alt={photos[previewIndex].name || `Image ${previewIndex + 1}`}
                  maxH='90vh'
                  maxW='70vw'
                  objectFit='contain'
                  borderRadius='md'
                />
                {photos.length > 1 && (
                  <IconButton
                    aria-label='Next image'
                    icon={<ChevronRightIcon w={10} h={10} />}
                    size='2xl'
                    isRound
                    bg='rgba(0, 0, 0, 0.8)'
                    color='white'
                    _hover={{ bg: "rgba(0, 0, 0, 0.95)" }}
                    onClick={handleNextImage}
                  />
                )}
              </HStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default JobLogRow;
